import { CheckCircleOutlined, CloseCircleOutlined, NodeIndexOutlined } from "@ant-design/icons";
import { useSearch } from "@tanstack/react-router";
import { Button, Tag } from "antd";
import {
    useExportIncomingPostbackStepLogsMutation,
    useGetIncomingPostbackStepLogsQuery,
} from "api/TenantSlug/StepLog/hooks";
import { IncomingPostbackStepLogDetailDrawer } from "components/Drawers/IncomingPostbackStepLogDetailDrawer/IncomingPostbackStepLogDetailDrawer";
import { useStepTypeLabel } from "components/UI/Selects/StepTypeSelectSearchable/useStepTypeLabel";
import { ShortenedIdWithCopy } from "components/UI/ShortenedIdWithCopy";
import { Table } from "components/UI/Table";
import { exportAtom } from "components/UI/Table/atoms/exportAtom";
import { DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, tableAtom } from "components/UI/Table/atoms/tableAtom";
import { TableColumn } from "components/UI/Table/columns/TableColumn.tsx";
import { FILTER_TYPES } from "components/UI/Table/constants/filter";
import { useTableDataApiOperations } from "components/UI/Table/hooks/useTableDataApiOperations";
import { TableColumnType } from "components/UI/Table/types/table";
import { ColumnHeaderTitle } from "components/UI/Table/UI/ColumnHeaderTitle";
import { DateTimeCell } from "components/UI/Table/UI/DateTimeCell/DateTimeCell";
import { EmptyCell } from "components/UI/Table/UI/EmptyCell/EmptyCell";
import { EntityLinkCell } from "components/UI/Table/UI/EntityLinkCell/EntityLinkCell";
import { StepLogMessageCell } from "components/UI/Table/UI/StepLogMessageCell/StepLogMessageCell";
import { useCreatedAtFilterValue } from "components/UI/Table/utils/getDefaultCreatedAtFilter";
import { TenantLink } from "components/UI/TenantLink/TenantLink";
import { usePaldockTranslation } from "hooks/usePaldockTranslation";
import { usePermission } from "hooks/usePermissions";
import { useRowHandlers } from "hooks/useTableRowHandlers";
import { Key } from "react";
import { useRecoilValue } from "recoil";
import { PermissionEnum, SearchFilterEnum } from "types/enums";
import { DrawerId, TableId } from "types/models";

import BaseExportRequest = App.Http.Requests.BaseExportRequest;
import GetStepLogIncomingPostbacksResponse = App.Http.Responses.StepLog.GetStepLogIncomingPostbacksResponse;
import StepLogIncomingPostbackDataOptional = App.Data.StepLogIncomingPostbackDataOptional;

interface SearchParams {
    [`incomingPostbackStepLogDetail-id`]: string;
}

export const IncomingPostbackStepLogTable = () => {
    const tableId = TableId.IncomingPostbackStepLogTable;
    const { t } = usePaldockTranslation("tables.incoming-postback-step-log");
    const { t: tG } = usePaldockTranslation("tables.general-columns");
    const { t: tGL } = usePaldockTranslation("tables.general-labels");
    const { t: tGA } = usePaldockTranslation("drawers.general-actions");
    const getStepTypeLabel = useStepTypeLabel();
    const tableAtomInstance = tableAtom<StepLogIncomingPostbackDataOptional>();
    const tableState = useRecoilValue(tableAtomInstance(tableId));
    const exportState = useRecoilValue(exportAtom(tableId));
    const permissionsUtils = usePermission();
    const createdAtStr = useCreatedAtFilterValue();
    const searchParams = useSearch({ strict: false }) as SearchParams;
    const canViewForeign = permissionsUtils.hasPermission(PermissionEnum.ConnectionSequenceStepLogForeign);

    const request = {
        page: {
            number: tableState.page?.number ?? DEFAULT_PAGE_NUMBER,
            size: tableState.page?.size ?? DEFAULT_PAGE_SIZE,
        },
        filter: {
            filter: {
                ...(tableState.filter?.filter ?? {}),
                ...("started_at" in (tableState.filter?.filter ?? {})
                    ? {}
                    : {
                          started_at: {
                              btw: createdAtStr,
                          },
                      }),
            },
            sort: tableState.filter?.sort ?? "-started_at",
            include: [
                "incomingPostback",
                "commission",
                "conversionState",
                "owner",
                canViewForeign ? "advertiser" : null,
                "offer",
                "stepRun",
            ]
                .filter(Boolean)
                .join(","),
            fields: {
                step_log_incoming_postbacks:
                    "id,origin_eid,eid,advertiser_custom_id,commission_id,owner_id,advertiser_id,offer_id,step_run_id,http_status_code,state,message,conversion_state_id,incoming_postback_id,started_at,created_at",
                incomingPostback: "id,name",
                commission: "id,description,commission_type,commission_group_id",
                conversionState: "id,type",
                owner: "id,nickname",
                ...(canViewForeign ? { advertiser: "id,nickname" } : {}),
                offer: "id,title",
                stepRun: "id,scenario_run_id,title,type",
            },
        },
    };

    const getIncomingPostbackStepLogsQuery = useGetIncomingPostbackStepLogsQuery(request, { enabled: false });
    const getIncomingPostbackStepLogsExportMutation = useExportIncomingPostbackStepLogsMutation();

    const tableFetchDataUtils = useTableDataApiOperations<
        GetStepLogIncomingPostbacksResponse,
        void,
        StepLogIncomingPostbackDataOptional,
        BaseExportRequest
    >(tableId, getIncomingPostbackStepLogsQuery, undefined, undefined, getIncomingPostbackStepLogsExportMutation);

    const isScenarioFilterActive = !!tableState.filter?.filter?.["eid"]?.[SearchFilterEnum.Equal];
    const scenarioRunId = tableFetchDataUtils.fetchQuery.isFetching
        ? undefined
        : tableFetchDataUtils.tableData?.incomingPostbackLogs?.[0]?.stepRun?.scenario_run_id;

    const viewScenarioRunButton = isScenarioFilterActive ? (
        scenarioRunId ? (
            <TenantLink to={`/scenario/history/${scenarioRunId}`}>
                <Button icon={<NodeIndexOutlined />}>{tGA("view_scenario_run")}</Button>
            </TenantLink>
        ) : (
            <Button icon={<NodeIndexOutlined />} disabled>
                {tGA("view_scenario_run")}
            </Button>
        )
    ) : null;

    const { getRowHandlers, getRowClassName } = useRowHandlers<StepLogIncomingPostbackDataOptional>({
        actions: [
            {
                type: "drawer",
                drawerId: DrawerId.IncomingPostbackStepLogDetail,
                drawerParam: `${DrawerId.IncomingPostbackStepLogDetail}-id`,
                permission: PermissionEnum.StepLogIncomingPostbacksView,
            },
        ],
    });

    const columnConfig: TableColumnType<StepLogIncomingPostbackDataOptional>[] = [
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.started")} />,
            dataIndex: "started_at",
            key: "started_at",
            render: (_, row) => <DateTimeCell value={row.started_at ?? undefined} />,
            sorter: true,
            defaultSorter: true,
            defaultSortOrder: "descend",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("created_at")} />,
            dataIndex: "created_at",
            key: "created_at",
            render: (v) => <DateTimeCell value={v} />,
            sorter: true,
            ellipsis: true,
            width: 150,
            visible: false,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("conversion_id")} />,
            dataIndex: "origin_eid",
            key: "origin_eid",
            render: (_, row) => (row.origin_eid ? <ShortenedIdWithCopy id={row.origin_eid} /> : <EmptyCell />),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "origin_eid",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("event_id")} />,
            dataIndex: "eid",
            key: "eid",
            render: (_, row) => (row.eid ? <ShortenedIdWithCopy id={row.eid} /> : <EmptyCell />),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "eid",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("run_id")} />,
            dataIndex: "step_run_id",
            key: "step_run_id",
            render: (_, row) => (row.step_run_id ? <ShortenedIdWithCopy id={row.step_run_id} /> : <EmptyCell />),
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "step_run_id",
            ellipsis: true,
            width: 150,
            visible: false,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("external_id")} />,
            dataIndex: "advertiser_custom_id",
            key: "advertiser_custom_id",
            render: (v) => v ?? <EmptyCell />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "advertiser_custom_id",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("commission_id")} />,
            dataIndex: "commission_id",
            key: "commission_id",
            render: (_, row) => <EntityLinkCell.Commission entity={row.commission} />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "commission_id",
            ellipsis: true,
            width: 120,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("transaction")} />,
            dataIndex: "conversionState.type",
            key: "conversionState.type",
            render: (_, row) => row.conversionState?.type ?? <EmptyCell />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "conversionState.type",
            ellipsis: true,
            width: 120,
        },
        TableColumn.Entity.Owner<StepLogIncomingPostbackDataOptional>({
            title: <ColumnHeaderTitle columnTitle={tG("affiliate")} />,
            dataIndex: "owner.nickname",
            key: "owner.nickname",
            filterName: "owner_id",
            sortName: "owner.nickname",
            ellipsis: true,
            width: 150,
            getEntity: (row) => (row.owner && row.owner_id ? { ...row.owner, id: row.owner_id } : undefined),
        }),
        ...(canViewForeign
            ? [
                  TableColumn.Entity.Advertiser<StepLogIncomingPostbackDataOptional>({
                      title: <ColumnHeaderTitle columnTitle={tG("advertiser")} />,
                      filterType: FILTER_TYPES.advertiserSelect,
                      ellipsis: true,
                      width: 150,
                      getEntity: (row) =>
                          row.advertiser && row.advertiser_id
                              ? { ...row.advertiser, id: row.advertiser_id }
                              : undefined,
                  }),
              ]
            : []),
        TableColumn.Entity.Offer<StepLogIncomingPostbackDataOptional>({
            title: <ColumnHeaderTitle columnTitle={tG("offer")} />,
            ellipsis: true,
            width: 150,
            getEntity: (row) => (row.offer && row.offer_id ? { ...row.offer, id: row.offer_id } : undefined),
        }),
        {
            title: <ColumnHeaderTitle columnTitle={tG("name")} />,
            dataIndex: "incomingPostback.name",
            key: "incomingPostback.name",
            render: (_, row) => <EntityLinkCell.IncomingPostback entity={row.incomingPostback} />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "incomingPostback.name",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.step")} />,
            dataIndex: "stepRun.title",
            key: "stepRun.title",
            render: (_, row) => row.stepRun?.title ?? <EmptyCell />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.string,
            filterName: "stepRun.title",
            ellipsis: true,
            width: 150,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.type")} />,
            dataIndex: "stepRun.type",
            key: "stepRun.type",
            render: (_, row) => {
                const type = row.stepRun?.type;
                if (!type) return <EmptyCell />;
                if (type === "integration" || type === "incoming_postback")
                    return getStepTypeLabel(type).split(" - ")[0];
                return getStepTypeLabel(type);
            },
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.incomingPostbackStepTypeSelect,
            filterName: "stepRun.type",
            ellipsis: true,
            width: 120,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("status")} />,
            dataIndex: "http_status_code",
            key: "http_status_code",
            render: (_, row) => row.http_status_code ?? <EmptyCell />,
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.number,
            filterName: "http_status_code",
            ellipsis: true,
            width: 100,
        },
        {
            title: <ColumnHeaderTitle columnTitle={tG("state")} />,
            dataIndex: "state",
            key: "state",
            render: (_, row) => {
                const isOk = row.state === true;
                return (
                    <Tag icon={isOk ? <CheckCircleOutlined /> : <CloseCircleOutlined />} color={isOk ? "green" : "red"}>
                        {isOk ? tGL("ok") : tGL("fail")}
                    </Tag>
                );
            },
            sorter: true,
            filterable: true,
            filterType: FILTER_TYPES.select,
            filterName: "state",
            filterOptions: [
                { label: tGL("ok"), value: "true" },
                { label: tGL("fail"), value: "false" },
            ],
            ellipsis: true,
            width: 100,
        },
        {
            title: <ColumnHeaderTitle columnTitle={t("columns.detail")} />,
            dataIndex: "message",
            key: "message",
            render: (_, row) => (
                <StepLogMessageCell message={row.message} stepType={row.stepRun?.type} id={`message-${row.id}`} />
            ),
            width: 390,
            ellipsis: true,
        },
    ];

    return (
        <>
            <Table<GetStepLogIncomingPostbacksResponse, StepLogIncomingPostbackDataOptional>
                tableId={tableId}
                columnConfig={columnConfig}
                data={tableFetchDataUtils.tableData as GetStepLogIncomingPostbacksResponse}
                dataSource={tableFetchDataUtils.tableData?.incomingPostbackLogs ?? []}
                onRefresh={tableFetchDataUtils.refetchData}
                showRefetchButton={true}
                rowKey={(record) => record.id as Key}
                onRow={(record, rowIndex) => getRowHandlers(record, rowIndex)}
                rowClassName={(_, rowIndex) => getRowClassName(rowIndex)}
                loading={tableFetchDataUtils.fetchQuery.isLoading || tableFetchDataUtils.fetchQuery.isFetching}
                onExport={() =>
                    tableFetchDataUtils.exportData({
                        filter: request.filter,
                        extension: exportState.extension,
                        headers: exportState.headers,
                        page: request.page,
                        export_all: exportState.range === "all",
                        delimiter: exportState.delimiter,
                    })
                }
                enableFilters={permissionsUtils.hasPermission(PermissionEnum.LogSelectFilter)}
                enableColumnsVisibility={permissionsUtils.hasPermission(PermissionEnum.LogColumnSetting)}
                enableTimeFilter={permissionsUtils.hasPermission(PermissionEnum.LogSelectFilterTime)}
                dateFilterDataIndex={"started_at"}
                dateFilterMode={"minute"}
                autoScrollWidth={true}
                hideActionsColumn={true}
                extraTopBarElements={viewScenarioRunButton}
                extraFilters={[]}
            />

            {permissionsUtils.hasPermission(PermissionEnum.StepLogIncomingPostbacksView) &&
                searchParams[`${DrawerId.IncomingPostbackStepLogDetail}-id`] && (
                    <IncomingPostbackStepLogDetailDrawer
                        logId={searchParams[`${DrawerId.IncomingPostbackStepLogDetail}-id`]}
                    />
                )}
        </>
    );
};
